import { FormBuilder, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export class MaterialPageGrid<T = any> {
  dataSource: MatTableDataSource<T> = new MatTableDataSource<T>([]);
  filtersForm: FormGroup;
  total = 0;
  pageSize = 10;
  pageIndex = 0;
  sortActive = '';
  sortDirection: 'asc' | 'desc' | '' = '';
  sortField = '';
  filterValues: any = {};
  displayedColumns: string[] = [];
  gridColumns = [];
  refreshDataInProgress = false;
  loading = false;
  error: string | null = null;
  private fetchData: (query: MaterialListQuery) => Promise<{ data: T[]; total: number }>;
  private fb: FormBuilder;
  private dataSubject = new BehaviorSubject<T[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  constructor(fb: FormBuilder, pageSize = 10) {
    this.fb = fb;
    this.pageSize = pageSize;
    this.filtersForm = this.fb.group({});
  }

  setFilters(filtersFormControls) {
    let previousFilterValues;
    this.filtersForm = this.fb.group(filtersFormControls);
    this.filtersForm.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((values) => {
      let currentFilterValues = JSON.stringify(values);
      if (previousFilterValues !== currentFilterValues) {
        previousFilterValues = currentFilterValues;
        this.filterValues = values;
        this.pageIndex = 0;
        this.refreshData();
      }
    });
    this.filterValues = this.filtersForm.value;
    this.refreshData();
    return this;
  }

  setSource(fetchData: (query: MaterialListQuery) => Promise<{ data: T[]; total: number }>) {
    this.fetchData = fetchData;
    this.refreshData();
    return this;
  }

  setTableColumns(columns: string[]) {
    this.displayedColumns = columns;
    return this;
  }

  attachSort(sort: MatSort) {
    this.dataSource.sort = sort;
    sort?.sortChange?.subscribe((sortEvent) => {
      this.sortActive = sortEvent.active;
      this.sortDirection = sortEvent.direction;
      if (this.sortActive) {
        this.sortField = `${this.sortActive || ''},${this.sortDirection || ''}`;
      }
      this.pageIndex = 0;
      this.refreshData();
    });
    return this;
  }
  onSortChange(event: { active: string; direction: 'asc' | 'desc' | '' }) {
    this.sortActive = event.active;
    this.sortDirection = event.direction;
    if (this.sortActive) {
      this.sortField = `${this.sortActive || ''},${this.sortDirection || ''}`;
    }
    this.pageIndex = 0;
    this.refreshData();
  }
  onPaginate(event: { pageIndex: number; pageSize: number }) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.refreshData();
  }
  attachPaginator(paginator: MatPaginator) {
    paginator?.page.subscribe((pageEvent) => {
      this.pageIndex = pageEvent.pageIndex;
      this.pageSize = pageEvent.pageSize;
      this.refreshData();
    });
    return this;
  }

  async refreshData() {
    if (!this.fetchData) return;

    this.refreshDataInProgress = true;
    this.loading = true;
    this.error = null;
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    try {
      const query: MaterialListQuery = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortActive: this.sortActive,
        sortDirection: this.sortDirection,
        filterValues: this.filterValues,
        sortField: this.sortField,
      };

      const { data, total } = await this.fetchData(query);
      this.dataSource.data = data;
      this.total = total;
      this.dataSubject.next(data);
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'An error occurred';
      this.errorSubject.next(this.error);
      this.dataSource.data = [];
      this.total = 0;
    } finally {
      this.refreshDataInProgress = false;
      this.loading = false;
      this.loadingSubject.next(false);
    }
  }

  get data$() {
    return this.dataSubject.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get error$(): Observable<string | null> {
    return this.errorSubject.asObservable();
  }

  // Utility methods
  clearFilters() {
    this.filtersForm.reset();
    this.pageIndex = 0;
    this.refreshData();
  }

  setPageSize(size: number) {
    this.pageSize = size;
    this.pageIndex = 0;
    this.refreshData();
  }

  goToPage(page: number) {
    this.pageIndex = page;
    this.refreshData();
  }

  getCurrentPageData(): T[] {
    return this.dataSource.data;
  }

  getTotalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  hasNextPage(): boolean {
    return this.pageIndex < this.getTotalPages() - 1;
  }

  hasPreviousPage(): boolean {
    return this.pageIndex > 0;
  }

  // Method to manually trigger refresh
  forceRefresh() {
    this.refreshData();
  }

  // Method to update specific filter value
  updateFilter(key: string, value: any) {
    this.filtersForm.get(key)?.setValue(value);
  }

  // Method to get current filter values
  getFilterValues() {
    return this.filtersForm.value;
  }
}

export interface MaterialListQuery {
  pageIndex: number;
  pageSize: number;
  sortActive: string;
  sortDirection: 'asc' | 'desc' | '';
  filterValues: any;
  sortField: string;
}

export interface MaterialGridConfig {
  pageSize?: number;
  pageSizeOptions?: number[];
  showFirstLastButtons?: boolean;
  hidePageSize?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  selectable?: boolean;
  multiSelect?: boolean;
}

export interface MaterialGridColumn {
  key: string;
  title: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  type?: 'text' | 'number' | 'date' | 'boolean' | 'custom';
  customTemplate?: string;
}

export interface MaterialGridAction {
  label: string;
  icon?: string;
  color?: 'primary' | 'accent' | 'warn';
  disabled?: boolean;
  onClick: (item: any) => void;
}
